/**
 * Mailing LISTS api — contract line #34 (`/api/mailing-lists…`, ADDITIVE v1.9.29,
 * T.Z131) plus the #33.1 template read the picker feeds on.
 *
 *   #34.1 GET  /api/mailing-lists                                      → `{ data:{ lists:[… + counts{total,renderReady}] } }`
 *   #34.2 POST /api/mailing-lists                                      → `{ data:{ list, resolve } }`
 *   #34.3 GET  /api/mailing-lists/:listId                              → `{ data, meta, list, counts }`  `?state=active|removed|all`
 *   #34.4 PATCH /api/mailing-lists/:listId                             → `{ data:{ list, resolve } }`
 *   #34.5 POST /api/mailing-lists/:listId/archive                      → `{ data:{ list } }`
 *   #34.6 POST /api/mailing-lists/:listId/resolve                      → `{ data:{ resolve } }`
 *   #34.7 GET  /api/mailing-lists/:listId/recipients/:applicationId/preview → `{ data:{ html, … } }`
 *   #33.1 GET  /api/email-templates                                    → `{ data:{ templates:[…] } }`
 *
 * The shapes mirror `apps/api/src/mailing/mailing-lists.*.ts` 1:1 (the generated
 * `api-types.ts` is not reachable from the bundle — `@cb-manager/shared` exports
 * only its barrel), the way `mailings.ts` mirrors #32.
 *
 * Error semantics the page surfaces verbatim (all NEW in v1.9.29 unless noted):
 *   below MANAGER            → 403 FORBIDDEN (the nav hides the item instead)
 *   bad (mode, payload) pair → 400 SELECTION_INVALID
 *   unknown pin              → 404 TEMPLATE_NOT_FOUND  (reused from #33)
 *   archived template        → 409 TEMPLATE_ARCHIVED   (reused from #33)
 *   unknown list             → 404 LIST_NOT_FOUND
 *   archived list write      → 409 LIST_ARCHIVED
 *
 * THE SEND (T.Z133, mounted since): this client probes for the engine
 * (`useMailingListSendEngine`, GET-only — see `lists-model.ts`), then carries
 * the three send verbs the engine answers — #34.8 test-send, #34.9 send-all,
 * #34.10 send-status — plus the status panel's one read. EVERY send answer
 * travels the house `{data:{…}}` envelope (T.Z185: the raw reads here once
 * typed the inner payload at the top level, and the page announced «undefined»
 * while the letters went out fine).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiRequest, buildQuery, requestEnvelope, type Envelope } from './client';
import { sendProbeAnswered } from '../pages/mailings/lists-model';

export const MAILING_LISTS_ROOT = '/mailing-lists';

/**
 * T.Z133 feature detection. The send engine is a SIBLING track that may land
 * before, with or after this UI; until its routes exist the buttons stay
 * visible but disabled with an honest hint (`SEND_UNAVAILABLE_HINT` in
 * `pages/mailings/lists-model.ts`), and nothing here ever POSTs to discover
 * them.
 *
 * The probe reads (GET only — a probe must never be able to send a letter):
 *   * `GET /api/mailing-lists/{listId}/send-status` — the run-status read the
 *     paced engine needs to report progress (the #32.2 precedent);
 *   * `GET /api/mailing-lists/{listId}/send` — a stray GET on the send route
 *     (Nest answers 404 to a method mismatch, so this arm only helps an engine
 *     that mounted a GET handler there).
 *
 * ANY non-404 answer means the route exists → the engine shipped. 404 on both
 * (or the network failing) keeps the hint. When T.Z133 mounts its reads
 * elsewhere, THIS constant is the only thing to repoint.
 */
export const SEND_PROBE_PATHS = (listId: number): ReadonlyArray<string> => [
  `${MAILING_LISTS_ROOT}/${listId}/send-status`,
  `${MAILING_LISTS_ROOT}/${listId}/send`,
];

export const SEND_TEST_PATH = (listId: number): string => `${MAILING_LISTS_ROOT}/${listId}/test-send`;
export const SEND_ALL_PATH = (listId: number): string => `${MAILING_LISTS_ROOT}/${listId}/send-all`;

export type MailingListSelectionMode = 'IDS' | 'STATUS' | 'STATUSES';
/** T.Z210 (ADDITIVE v1.9.72) — the channel a list rides / a template speaks. */
export type MailingChannel = 'EMAIL' | 'SMS';
export type MailingListStateFilter = 'active' | 'removed' | 'all';
export type MailingTrack = 'AI' | 'PYTHON';

/** The pinned template VERSION — the compound FK the list can never lose. */
export interface MailingListTemplatePinDto {
  templateId: number;
  templateVersion: number;
  key: string;
  title: string;
}

/** One list (#34.1/#34.2/#34.4/#34.5). */
export interface MailingListDto {
  id: number;
  /** T.Z210 — the channel; absent on an api older than v1.9.72 (an EMAIL list). */
  channel?: MailingChannel;
  name: string;
  /** The user-EDITABLE label (e.g. «Рассылка не подписавшим договор»). */
  mailingType: string;
  fromEmail: string;
  template: MailingListTemplatePinDto;
  selectionMode: MailingListSelectionMode;
  /** `{ids:[…]}` / `{status:…}` / `{statuses:[…]}` — what the api validated and stored. */
  selectionPayload: Record<string, unknown>;
  archivedAt: string | null;
  createdByUserId: number | null;
  lastResolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** The counts of ONE list's snapshot (#34.3). */
export interface MailingListCountsDto {
  total: number;
  removed: number;
  renderReady: number;
  byStatus: {
    waitingLink: number;
    linkReceived: number;
    eviPassed: number;
    rejected: number;
    awaitingInfo: number;
    noData: number;
  };
  byTrack: { ai: number; python: number; unresolved: number };
}

/** The index row's two head numbers (#34.1). */
export interface MailingListHeadCounts {
  total: number;
  renderReady: number;
}

/** ONE resolved recipient — the snapshot row, as the resolve FROZE it. */
export interface MailingListRecipientDto {
  applicationId: number;
  pguId: string | null;
  untiId: string | null;
  fullName: string | null;
  email: string | null;
  /** T.Z210 — the number an SMS goes to (raw; the api normalises at send time). */
  phone?: string | null;
  statusAtResolve: 'WAITING_LINK' | 'LINK_RECEIVED' | 'EVI_PASSED' | 'REJECTED' | 'AWAITING_INFO' | 'NO_DATA';
  programName: string | null;
  track: MailingTrack;
  trackResolved: boolean;
  greetingName: string | null;
  renderReady: boolean;
  resolvedAt: string;
  removedAt: string | null;
  /** ADDITIVE v1.9.73 (T.Z211) — present ONLY when the child owns at least one
   * short-link code of THIS list: true = at least one of them was opened. */
  clicked?: boolean;
  /** ADDITIVE v1.9.73 (T.Z211) — the FIRST click over every code and run of the
   * list (null = the codes exist, none opened yet). */
  clickedAt?: string | null;
}

/** The diff ONE resolution run reports (#34.2/#34.4/#34.6). */
export interface MailingListResolveReportDto {
  mode: MailingListSelectionMode;
  total: number;
  added: number;
  refreshed: number;
  reactivated: number;
  removed: number;
  requestedIds: number | null;
  missingCount: number | null;
  missingIds: string[] | null;
  resolvedAt: string;
}

export interface MailingListPreviewDto {
  listId: number;
  applicationId: number;
  /** T.Z210 — `SMS` means `html` carries the plain TEXT of the message. */
  channel?: MailingChannel;
  subject: string;
  html: string;
  startUrl: string;
  greetingName: string | null;
  track: MailingTrack;
  trackResolved: boolean;
  template: MailingListTemplatePinDto;
  recipient: MailingListRecipientDto;
}

export interface MailingListCreateInput {
  name: string;
  /** T.Z210 — the channel; absent on the wire = EMAIL (the pre-T.Z210 shape). */
  channel?: MailingChannel;
  mailingType: string;
  fromEmail: string;
  templateId: number;
  templateVersion: number;
  selectionMode: MailingListSelectionMode;
  selectionPayload: Record<string, unknown>;
}

/** #34.4 — every field optional; at least one must be present; ANY change re-resolves. */
export interface MailingListPatchInput {
  name?: string;
  /** T.Z210 — the pinned template's channel must agree (the api answers 400). */
  channel?: MailingChannel;
  mailingType?: string;
  fromEmail?: string;
  templateId?: number;
  templateVersion?: number;
  selectionMode?: MailingListSelectionMode;
  selectionPayload?: Record<string, unknown>;
}

// --------------------------------------------------------------- reads -----

/** #34.1 — every list, live first, unpaginated, with its head counts. */
export function useMailingLists() {
  return useQuery({
    queryKey: ['mailing-lists', 'index'],
    queryFn: async ({ signal }) => {
      const answer = await requestEnvelope<{ lists?: unknown }>('GET', MAILING_LISTS_ROOT, ['data'], { signal });
      // Normalize: a body without a list array renders an EMPTY table, never a
      // crash (the classic `/api/mailings` route pattern can answer this path
      // in a stubbed test run — the shape guard keeps that honest, not fatal).
      return { lists: Array.isArray(answer.data?.lists) ? (answer.data.lists as (MailingListDto & { counts: MailingListHeadCounts })[]) : [] };
    },
  });
}

export interface MailingListPage {
  data: MailingListRecipientDto[];
  meta: { page: number; pageSize: number; total: number };
  list: MailingListDto;
  counts: MailingListCountsDto;
}

/** #34.3 — one page of a list's recipients + the list + the counts. The envelope
 * here carries `meta`/`list`/`counts` alongside `data`, so `requestEnvelope`
 * (which keeps only the payload key) would drop them — ONE raw read, the way
 * `fetchMailingsList` reads #32.1. */
export function fetchMailingList(
  listId: number,
  params: { state?: MailingListStateFilter; page?: number; pageSize?: number } = {},
  signal?: AbortSignal,
): Promise<MailingListPage> {
  const query = buildQuery({
    ...(params.state ? { state: params.state } : {}),
    ...(params.page ? { page: String(params.page) } : {}),
    ...(params.pageSize ? { pageSize: String(params.pageSize) } : {}),
  });
  return apiRequest<MailingListPage>('GET', `${MAILING_LISTS_ROOT}/${listId}${query}`, { signal });
}

/**
 * #33.1 — the templates the picker feeds on live in their OWN client since
 * T.Z134 (`./email-templates`, which also owns the writes and the version read
 * the templates tab needs). One hook, one query key (`['email-templates',
 * 'index']`), one cache: a template created on the «Шаблоны» tab is in this
 * picker the moment the cache invalidates.
 */

// -------------------------------------------------------------- writes -----

/** #34.2 — create + first resolution in one call. */
export function useCreateMailingList() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: MailingListCreateInput): Promise<Envelope<{ list: MailingListDto; resolve: MailingListResolveReportDto }>> =>
      apiRequest<Envelope<{ list: MailingListDto; resolve: MailingListResolveReportDto }>>('POST', MAILING_LISTS_ROOT, {
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['mailing-lists'] }),
  });
}

/** #34.4 — patch; the answer carries the report of the re-resolution it triggered. */
export function usePatchMailingList() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      listId,
      ...input
    }: MailingListPatchInput & { listId: number }): Promise<
      Envelope<{ list: MailingListDto; resolve: MailingListResolveReportDto }>
    > =>
      apiRequest<Envelope<{ list: MailingListDto; resolve: MailingListResolveReportDto }>>(
        'PATCH',
        `${MAILING_LISTS_ROOT}/${listId}`,
        { headers: { 'content-type': 'application/json' }, body: JSON.stringify(input) },
      ),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['mailing-lists'] }),
  });
}

/** #34.5 — the archive toggle (there is NO delete). Restoring does not re-resolve. */
export function useArchiveMailingList() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ listId, archived }: { listId: number; archived: boolean }) =>
      requestEnvelope<{ list: MailingListDto }>('POST', `${MAILING_LISTS_ROOT}/${listId}/archive`, ['data'], {
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ archived }),
      }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['mailing-lists'] }),
  });
}

/** #34.6 — the manual refresh; the diff answers verbatim. */
export function useResolveMailingList() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ listId }: { listId: number }) =>
      requestEnvelope<{ resolve: MailingListResolveReportDto }>('POST', `${MAILING_LISTS_ROOT}/${listId}/resolve`, ['data']),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['mailing-lists'] }),
  });
}

/** #34.7 — the rendered letter for one resolved recipient, through the pinned version. */
export function useMailingListPreview(listId: number | null, applicationId: number | null) {
  return useQuery({
    queryKey: ['mailing-lists', 'preview', listId, applicationId],
    enabled: listId !== null && applicationId !== null,
    queryFn: ({ signal }) =>
      requestEnvelope<MailingListPreviewDto>(
        'GET',
        `${MAILING_LISTS_ROOT}/${listId}/recipients/${applicationId}/preview`,
        ['data'],
        { signal },
      ),
  });
}

// ---------------------------------------------------- the send engine (T.Z133) —

/**
 * T.Z133 feature detection — GET-only probes over the paths the engine is
 * expected to mount (the reasoning and the path list live in ONE place,
 * `SEND_PROBE_PATHS`). Any non-404 answer means the route exists; 404 on both
 * (or a network failure) keeps the buttons disabled with the hint.
 *
 * A probe can never send a letter: both arms are GETs, and the send route is a
 * POST. The result is kept for the session (no refetch churn while the
 * operator pages the table).
 */
export type SendEngineProbe = { available: boolean; checkedAt: string };

export function useMailingListSendEngine(listId: number | null) {
  return useQuery({
    queryKey: ['mailing-lists', 'send-engine', listId],
    enabled: listId !== null,
    staleTime: 5 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async ({ signal }): Promise<SendEngineProbe> => {
      if (listId === null) return { available: false, checkedAt: new Date().toISOString() };
      for (const path of SEND_PROBE_PATHS(listId)) {
        try {
          const response = await fetch(`/api${path}`, {
            method: 'GET',
            headers: { Accept: 'application/json' },
            signal,
          });
          if (sendProbeAnswered(response.status)) return { available: true, checkedAt: new Date().toISOString() };
        } catch {
          // A dead probe is an ABSENT engine, not an error the page should raise.
        }
      }
      return { available: false, checkedAt: new Date().toISOString() };
    },
  });
}

export interface MailingListSendTestInput {
  listId: number;
  applicationId: number;
  /** The e-mail list's test address. */
  email?: string;
  /** T.Z210 — the SMS list's test number (the operator's own phone). */
  phone?: string;
}

/** The #34.8 answer (the mirror of `MailingListTestSendResponse`). */
export interface MailingListTestSendDto {
  sent: boolean;
  listId: number;
  applicationId: number;
  to: string;
  startUrl: string;
}

/**
 * T.Z133 — the test letter from this list's snapshot (mounted when the engine
 * ships). The api answers the HOUSE ENVELOPE (`{data:{…}}`) — T.Z185: the raw
 * read here was the «Тест-письмо ушло на undefined» bug, the same shape slip the
 * send-all notice had.
 */
export function useMailingListTestSend() {
  return useMutation({
    mutationFn: async ({ listId, ...input }: MailingListSendTestInput): Promise<Envelope<MailingListTestSendDto>> =>
      apiRequest<Envelope<MailingListTestSendDto>>('POST', SEND_TEST_PATH(listId), {
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      }),
  });
}

/** The #34.9 answer (the mirror of `MailingListSendAllResponse`). */
export interface MailingListSendAllDto {
  queued: boolean;
  runId: string;
  listId: number;
  total: number;
  skippedAlreadySent: number;
}

/** T.Z133 — arm the paced send over this list's live recipients (202 when armed).
 *
 * T.Z185 — the shape slip behind «Рассылка запущена: undefined получателей
 * (запуск undefined)»: the api wraps `{queued, runId, listId, total,
 * skippedAlreadySent}` in the house `data` envelope (the documented
 * `MailingListSendAllEnvelope`), while this read typed the INNER payload at the
 * top level and the view read `answer.total`/`answer.runId` off the envelope —
 * always `undefined`. The type now says what the wire carries; the view reads
 * `answer.data.*`, the fixed panels' own grammar. */
export function useMailingListSendAll() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ listId }: { listId: number }): Promise<Envelope<MailingListSendAllDto>> =>
      apiRequest<Envelope<MailingListSendAllDto>>('POST', SEND_ALL_PATH(listId), {
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({}),
      }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['mailing-lists'] }),
  });
}

/** The #34.10 payload — the run progress + THIS list's delivery totals. */
export interface MailingListSendStatusDto {
  running: boolean;
  run: {
    runId: string;
    listId: number;
    startedAt: string;
    finishedAt: string | null;
    total: number;
    processed: number;
    accepted: number;
    failed: number;
    skipped: number;
  } | null;
  /** ADDITIVE v1.9.56 — `notReceived` = the live recipients not yet served by THIS list. */
  ledger: { accepted: number; delivered: number; failed: number; skipped: number; notReceived: number };
  /** ADDITIVE v1.9.73 (T.Z211) — the SHORT-LINK clicks facts, recipient-shaped:
   * `linked` = live recipients whose child owns a code of this list, `clicked` =
   * of those, the ones whose code was opened, `notClicked` = linked − clicked.
   * May be absent on an api that predates the field. */
  clicks?: { linked: number; clicked: number; notClicked: number };
}

/**
 * #34.10 — the run progress + THIS list's ledger totals (the status panel's one
 * read). Polls every 2 s only while a run is draining, the fixed panels' own
 * rule; a list whose engine never shipped simply never calls it (the probe
 * gates the buttons, and the panel renders the honest zeros).
 */
export function useMailingListSendStatus(listId: number) {
  return useQuery({
    queryKey: ['mailing-lists', 'send-status', listId],
    queryFn: ({ signal }) =>
      requestEnvelope<MailingListSendStatusDto>('GET', `${MAILING_LISTS_ROOT}/${listId}/send-status`, ['data'], { signal }),
    refetchInterval: (query) => (query.state.data?.data.running ? 2000 : false),
  });
}

// ------------------------------------------------- the SMS channel (T.Z210) —

/** The sign facts the page's SMS line renders (#34.12, ADDITIVE v1.9.72). */
export interface MailingListSmsSignsDto {
  configured: boolean;
  sign: string | null;
  registered: { ok: true; names: string[] } | { ok: false; error: string };
}

/**
 * #34.12 — the read-only SMS capability probe (the e-mail panel's
 * delivery-capabilities precedent): the configured sign, the signs the account
 * has REGISTERED, and whether the driver has credentials at all. Cached for the
 * session — it answers facts an operator changes by redeploying, not by
 * clicking.
 */
export function useMailingListSmsSigns() {
  return useQuery({
    queryKey: ['mailing-lists', 'sms-signs'],
    staleTime: 5 * 60 * 1000,
    retry: false,
    queryFn: ({ signal }) =>
      requestEnvelope<MailingListSmsSignsDto>('GET', `${MAILING_LISTS_ROOT}/sms/signs`, ['data'], { signal }),
  });
}
